"use client";

import {
  m as motion,
  useInView,
  useReducedMotion,
  type Variants,
} from "framer-motion";
import { useRef } from "react";
import Link from "next/link";
import {
  Map,
  Hammer,
  TrendingUp,
  ArrowRight,
  MessageSquare,
  Clock,
  User,
  PhoneCall,
} from "lucide-react";
import { MetodoFrameworkVisual } from "./MetodoFrameworkVisual";
import { InBreve } from "@/components/shared/InBreve";
import { ServiceMotif } from "@/components/services/ServiceMotif";
import { Button } from "@/components/ui/button";
import { trackEvent } from "@/lib/track";

interface MetodoContentProps {
  isItalian: boolean;
  bookingLink: string;
}

// Cadenza ~0.22s tra i token del titolo; durata ~0.5s per token.
const TITLE_TOKEN_CADENCE = 0.22;

export function MetodoContent({ isItalian, bookingLink }: MetodoContentProps) {
  const reduceMotion = useReducedMotion();
  const titleRef = useRef(null);
  const titleInView = useInView(titleRef, { once: true });

  // Variante container del titolo: rivela i token in sequenza (stagger).
  const titleContainerVariants: Variants = {
    hidden: {},
    visible: {
      transition: reduceMotion ? {} : { staggerChildren: TITLE_TOKEN_CADENCE },
    },
  };

  // Variante per ogni token del titolo: dissolvenza + leggero rise.
  const tokenVariants: Variants = reduceMotion
    ? { hidden: { opacity: 1, y: 0 }, visible: { opacity: 1, y: 0 } }
    : {
        hidden: { opacity: 0, y: 8 },
        visible: {
          opacity: 1,
          y: 0,
          transition: { duration: 0.5, ease: [0.16, 1, 0.3, 1] },
        },
      };

  const titleAnimateState = titleInView || reduceMotion ? "visible" : "hidden";

  const steps = isItalian
    ? [
        {
          icon: Map,
          step: "01",
          title: "Mappa",
          subtitle: "Capire e definire",
          description:
            "Ogni progetto inizia capendo bene il contesto. Non scrivo codice prima di aver capito dove vuoi arrivare.",
          activities: [
            "Call iniziale per capire obiettivi e vincoli",
            "Analisi di cosa hai già (se c'è)",
            "Definiamo cosa faccio di preciso e cosa ricevi",
            "Proposta chiara con tempi realistici",
            "Ci accordiamo su priorità e obiettivi",
          ],
          duration: "1-3 giorni",
          output: "Obiettivi chiari + proposta",
        },
        {
          icon: Hammer,
          step: "02",
          title: "Costruisci",
          subtitle: "Realizzo e verifico",
          description:
            "Lo costruisco un pezzo alla volta, sempre sotto i tuoi occhi. Controllo ogni parte prima di consegnartela, pronta all'uso.",
          activities: [
            "Sviluppo a piccoli passi, con punti di controllo",
            "Controllo ogni parte prima di consegnarla",
            "Verifica di qualità su tutto",
            "Istruzioni chiare per usarlo",
            "Ti mostro i progressi e raccolgo il tuo parere",
          ],
          duration: "Variabile per progetto",
          output: "Lavoro pronto all'uso, verificato e documentato",
        },
        {
          icon: TrendingUp,
          step: "03",
          title: "Evolvi",
          subtitle: "Crescita nel tempo",
          description:
            "Il lancio è l'inizio, non la fine. I progetti migliori crescono nel tempo con miglioramenti basati su cosa funziona davvero.",
          activities: [
            "Messa online guidata, passo per passo",
            "Controllo iniziale e correzione problemi",
            "Raccolta feedback utenti reali",
            "Prioritizzazione miglioramenti",
            "Supporto continuativo (opzionale)",
          ],
          duration: "Continuo",
          output: "Miglioramenti nel tempo + supporto",
        },
      ]
    : [
        {
          icon: Map,
          step: "01",
          title: "Map",
          subtitle: "Understand & define",
          description:
            "Every project starts by understanding the context. I do not write code before we define where you want to go.",
          activities: [
            "Initial call to understand goals and constraints",
            "A look at what you already have (if any)",
            "We define exactly what I do and what you get",
            "Clear proposal with realistic timing",
            "We agree on priorities and goals",
          ],
          duration: "1-3 days",
          output: "Clear goals + proposal",
        },
        {
          icon: Hammer,
          step: "02",
          title: "Build",
          subtitle: "Build & check",
          description:
            "I build it one piece at a time, always in front of you. I check every part before handing it over, ready to use.",
          activities: [
            "Step-by-step development, with checkpoints",
            "I check every part before handing it over",
            "Quality check on everything",
            "Clear instructions to use it",
            "I show you progress and gather your feedback",
          ],
          duration: "Varies by project",
          output: "Work ready to use, checked and documented",
        },
        {
          icon: TrendingUp,
          step: "03",
          title: "Evolve",
          subtitle: "Growth over time",
          description:
            "Launch is the beginning, not the end. The best projects grow over time with improvements based on what actually works.",
          activities: [
            "Guided go-live, step by step",
            "Initial checks and bug fixing",
            "Real user feedback collection",
            "Improvement prioritization",
            "Continuous support (optional)",
          ],
          duration: "Ongoing",
          output: "Improvements over time + support",
        },
      ];

  const principles = isItalian
    ? [
        {
          icon: MessageSquare,
          title: "Comunicazione asincrona",
          description:
            "Comunicazione scritta e documentata, senza riunioni inutili. Aggiornamenti regolari, risposte entro 24h.",
        },
        {
          icon: User,
          title: "Accesso diretto",
          description:
            "Lavori con me, non con un account manager. Un unico interlocutore responsabile dall'inizio alla fine.",
        },
        {
          icon: Clock,
          title: "Tempi chiari",
          description:
            "Timeline realistiche, non promesse gonfiate. Se qualcosa cambia, lo saprai subito.",
        },
      ]
    : [
        {
          icon: MessageSquare,
          title: "Async-first",
          description:
            "Written and documented communication, no unnecessary meetings. Regular updates, replies within 24h.",
        },
        {
          icon: User,
          title: "Direct access",
          description:
            "You work with me, not with an account manager. One accountable partner from start to finish.",
        },
        {
          icon: Clock,
          title: "Clear timing",
          description:
            "Realistic timelines, no inflated promises. If anything changes, you know immediately.",
        },
      ];

  return (
    <>
      <section className="section-padding pt-8 md:pt-24 xl:min-h-[calc(100dvh-5.5rem)] xl:flex xl:flex-col xl:justify-center xl:py-0 relative overflow-hidden">
        <div className="absolute inset-0 pattern-grid opacity-40" />
        <div className="absolute top-0 left-0 w-[460px] h-[460px] bg-primary/7 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-0 w-[320px] h-[320px] bg-accent/8 rounded-full blur-3xl" />

        <div className="container-wide relative">
          <motion.span
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="font-mono text-label text-primary mb-4 block text-left"
          >
            {isItalian ? "Metodo MCE" : "MCE Method"}
          </motion.span>

          <h1
            ref={titleRef}
            className="font-display font-bold tracking-tight leading-tight mb-10 text-left"
          >
            <motion.span
              className="flex flex-col gap-3 text-4xl sm:text-5xl md:hidden"
              variants={titleContainerVariants}
              initial="hidden"
              animate={titleAnimateState}
            >
              <motion.span
                variants={tokenVariants}
                className="mce-flow self-start"
                style={{ animationDelay: "0s" }}
              >
                <span className="text-shine">M</span>
                {isItalian ? "appa" : "ap"}
              </motion.span>
              <motion.span
                variants={tokenVariants}
                className="mce-flow self-center"
                style={{ animationDelay: "0.4s" }}
              >
                <span className="text-shine">{isItalian ? "C" : "B"}</span>
                {isItalian ? "ostruisci" : "uild"}
              </motion.span>
              <motion.span
                variants={tokenVariants}
                className="mce-flow self-end"
                style={{ animationDelay: "0.8s" }}
              >
                <span className="text-shine">E</span>
                {isItalian ? "volvi" : "volve"}
              </motion.span>
            </motion.span>
            <motion.span
              className="hidden md:inline md:whitespace-nowrap md:text-[2.4rem] lg:text-5xl xl:text-6xl"
              variants={titleContainerVariants}
              initial="hidden"
              animate={titleAnimateState}
            >
              {isItalian ? (
                <>
                  <motion.span
                    variants={tokenVariants}
                    className="mce-flow inline-block"
                    style={{ animationDelay: "0s" }}
                  >
                    <span className="text-shine">M</span>appa
                  </motion.span>{" "}
                  <motion.span
                    variants={tokenVariants}
                    className="mce-flow inline-block text-primary/50"
                    style={{ animationDelay: "0.26s" }}
                  >
                    &rarr;
                  </motion.span>{" "}
                  <motion.span
                    variants={tokenVariants}
                    className="mce-flow inline-block"
                    style={{ animationDelay: "0.52s" }}
                  >
                    <span className="text-shine">C</span>ostruisci
                  </motion.span>{" "}
                  <motion.span
                    variants={tokenVariants}
                    className="mce-flow inline-block text-primary/50"
                    style={{ animationDelay: "0.78s" }}
                  >
                    &rarr;
                  </motion.span>{" "}
                  <motion.span
                    variants={tokenVariants}
                    className="mce-flow inline-block"
                    style={{ animationDelay: "1.04s" }}
                  >
                    <span className="text-shine">E</span>volvi
                  </motion.span>
                </>
              ) : (
                <>
                  <motion.span
                    variants={tokenVariants}
                    className="mce-flow inline-block"
                    style={{ animationDelay: "0s" }}
                  >
                    <span className="text-shine">M</span>ap
                  </motion.span>{" "}
                  <motion.span
                    variants={tokenVariants}
                    className="mce-flow inline-block text-primary/50"
                    style={{ animationDelay: "0.26s" }}
                  >
                    &rarr;
                  </motion.span>{" "}
                  <motion.span
                    variants={tokenVariants}
                    className="mce-flow inline-block"
                    style={{ animationDelay: "0.52s" }}
                  >
                    <span className="text-shine">B</span>uild
                  </motion.span>{" "}
                  <motion.span
                    variants={tokenVariants}
                    className="mce-flow inline-block text-primary/50"
                    style={{ animationDelay: "0.78s" }}
                  >
                    &rarr;
                  </motion.span>{" "}
                  <motion.span
                    variants={tokenVariants}
                    className="mce-flow inline-block"
                    style={{ animationDelay: "1.04s" }}
                  >
                    <span className="text-shine">E</span>volve
                  </motion.span>
                </>
              )}
            </motion.span>
          </h1>

          <div className="grid items-center gap-10 md:grid-cols-2">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="max-w-xl text-left"
            >
              <p className="text-body-lg text-muted-foreground mb-8 leading-relaxed">
                {isItalian ? (
                  <>
                    Un processo semplice e{" "}
                    <span className="font-medium text-primary">asincrono</span>, con{" "}
                    <span className="font-medium text-primary">accesso diretto</span> a chi fa il
                    lavoro e <span className="font-medium text-primary">un unico responsabile</span>{" "}
                    dall&apos;inizio alla fine. Mappo, costruisco ed evolvo il tuo progetto un passo
                    alla volta, sempre sotto i tuoi occhi.
                  </>
                ) : (
                  <>
                    A simple and{" "}
                    <span className="font-medium text-primary">asynchronous</span> process, with{" "}
                    <span className="font-medium text-primary">direct access</span> to the person
                    doing the work and{" "}
                    <span className="font-medium text-primary">one accountable owner</span> from
                    start to finish. I map, build and evolve your project one step at a time, always
                    in front of you.
                  </>
                )}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-start">
                <Button variant="hero" size="lg" asChild>
                  <Link
                    href={bookingLink}
                    onClick={() =>
                      trackEvent("book_call_click", {
                        tool: "calendly",
                        location: "metodo_hero",
                      })
                    }
                  >
                    {isItalian ? "Prenota una call" : "Book a call"}
                    <PhoneCall className="w-4 h-4" />
                  </Link>
                </Button>
              </div>
            </motion.div>

            <div className="flex items-center justify-center">
              <MetodoFrameworkVisual isItalian={isItalian} />
            </div>
          </div>
        </div>
      </section>

      <InBreve
        text={
          isItalian
            ? "Qui vedi come lavoro davvero: priorità chiare fin dalla prima chiacchierata, parli sempre con me e non con un intermediario, e ricevi un prezzo deciso prima con tempi chiari, senza sorprese a fine lavoro. Niente riunioni inutili e niente burocrazia: un unico referente che segue il progetto dall'inizio alla consegna, concentrato su risultati che puoi misurare. Vuoi capire se è il modo giusto per il tuo progetto? Prenotiamo una call di 30 minuti e ne parliamo, senza impegno."
            : "Here you see how I actually work: clear priorities from our very first chat, you always talk to me and not to a middleman, and you get a price agreed up front with clear timing, no surprises when the work is done. No pointless meetings and no red tape: one single point of contact who follows the project from start to delivery, focused on results you can measure. Want to find out if this is the right fit for your project? Let's book a 30-minute call and talk it through, no strings attached."
        }
        aside={
          <div className="relative h-40 w-40 md:h-48 md:w-48">
            <div className="pointer-events-none absolute inset-0 rounded-full bg-primary/10 blur-2xl" />
            <div className="relative h-full w-full">
              <ServiceMotif variant="target" />
            </div>
          </div>
        }
      />

      <section className="section-padding pt-0">
        <div className="container-wide">
          <div className="relative">
            {/* Rail verticale del percorso (desktop) */}
            <div className="pointer-events-none absolute left-5 top-6 bottom-6 hidden w-px bg-gradient-to-b from-primary/50 via-primary/25 to-transparent md:block" />

            <div className="space-y-6">
              {steps.map((step, index) => (
                <motion.div
                  key={step.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 0.5, delay: 0.1 * index }}
                  className="relative md:pl-16"
                  data-snap-anchor
                >
                  {/* Nodo numerato sul rail */}
                  <div className="absolute left-0 top-8 z-10 hidden h-10 w-10 items-center justify-center rounded-full border border-primary/40 bg-background font-mono text-sm font-bold text-primary shadow-[0_0_18px_hsl(var(--primary)/0.18)] md:flex">
                    {step.step}
                  </div>

                  <div className="group surface-card p-8 md:p-10">
                    <div className="grid gap-8 xl:grid-cols-3">
                      <div className="xl:col-span-2">
                        <div className="mb-5 flex items-center gap-3">
                          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-primary to-primary-glow">
                            <step.icon className="h-6 w-6 text-primary-foreground" />
                          </div>
                          <div>
                            <h2 className="font-display text-2xl font-bold leading-none">
                              {step.title}
                            </h2>
                            <span className="mt-1.5 block font-mono text-xs uppercase tracking-wider text-primary/80">
                              {step.subtitle}
                            </span>
                          </div>
                        </div>
                        <p className="text-body text-muted-foreground mb-6">{step.description}</p>

                        <h3 className="font-display font-semibold text-sm mb-3 text-muted-foreground uppercase tracking-wider">
                          {isItalian ? "Attività" : "Activities"}
                        </h3>
                        <ul className="space-y-2">
                          {step.activities.map((activity) => (
                            <li key={activity} className="flex items-start gap-2">
                              <ArrowRight className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                              <span className="text-sm">{activity}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div className="space-y-6 xl:border-l xl:border-[hsl(var(--border))] xl:pl-8">
                        <div>
                          <h3 className="font-display font-semibold text-sm mb-2 text-muted-foreground uppercase tracking-wider">
                            {isItalian ? "Durata tipica" : "Typical duration"}
                          </h3>
                          <p className="text-lg font-display font-semibold text-primary">
                            {step.duration}
                          </p>
                        </div>
                        <div>
                          <h3 className="font-display font-semibold text-sm mb-2 text-muted-foreground uppercase tracking-wider">
                            {isItalian ? "Risultato" : "Output"}
                          </h3>
                          <p className="text-sm text-muted-foreground">{step.output}</p>
                        </div>
                        <div className="hidden justify-center pt-2 xl:flex">
                          <div className="relative h-20 w-20">
                            <div className="pointer-events-none absolute inset-0 rounded-full bg-primary/10 blur-xl" />
                            <div className="relative h-full w-full">
                              <ServiceMotif variant={["nodes", "rebuild", "target"][index] ?? "nodes"} />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="section-padding bg-surface/30">
        <div className="container-wide">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-left mb-12"
            data-snap-anchor
          >
            <div className="relative mx-auto xl:mx-0 mt-2 mb-8 xl:mt-0 xl:mb-6 h-[12.5rem] w-[12.5rem] sm:h-[15rem] sm:w-[15rem] xl:h-28 xl:w-28">
              <div className="pointer-events-none absolute inset-0 rounded-full bg-primary/10 blur-2xl" />
              <div className="relative h-full w-full">
                <ServiceMotif variant="cube" />
              </div>
            </div>
            <h2 className="font-display text-h2 font-bold mb-4">
              {isItalian ? "Principi di lavoro" : "Working principles"}
            </h2>
            <p className="text-body-lg text-muted-foreground max-w-xl">
              {isItalian
                ? "Come preferisco lavorare per garantire risultati eccellenti."
                : "How I work to deliver excellent outcomes."}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {principles.map((principle, index) => (
              <motion.div
                key={principle.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 * index }}
                className="surface-card p-8 text-left"
                data-snap-anchor
              >
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-6">
                  <principle.icon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-display text-xl text-gradient font-semibold mb-2">
                  {principle.title}
                </h3>
                <p className="text-sm text-muted-foreground">{principle.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding pb-24 md:pb-32" data-snap-label="CTA">
        <div className="container-wide">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="surface-card relative overflow-hidden p-8 md:p-12"
          >
            <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-primary/10 blur-3xl" />
            <div className="relative grid items-center gap-8 xl:grid-cols-[1.3fr_0.7fr]">
              <div className="text-left">
                <h2 className="page-cta-title mb-4">
                  {isItalian ? "Ti sembra un buon match?" : "Does this feel like a fit?"}
                </h2>
                <p className="text-body-lg text-muted-foreground mb-8 max-w-lg mx-0">
                  {isItalian
                    ? "Parliamone. 30 minuti per capire se possiamo lavorare insieme, con priorità e prossimo passo chiari."
                    : "Let's talk. 30 minutes to understand if we can work together, with clear priorities and a clear next step."}
                </p>
                <Button variant="hero" size="xl" className="w-full sm:w-auto" asChild>
                  <Link
                    href={bookingLink}
                    onClick={() =>
                      trackEvent("book_call_click", {
                        tool: "calendly",
                        location: "cta_section",
                      })
                    }
                  >
                    {isItalian ? "Prenota una call" : "Book a call"}
                    <PhoneCall className="w-4 h-4" />
                  </Link>
                </Button>
              </div>
              <div className="flex items-center justify-center">
                <div className="relative h-40 w-40 md:h-52 md:w-52">
                  <div className="pointer-events-none absolute inset-0 rounded-full bg-primary/10 blur-2xl" />
                  <div className="relative h-full w-full">
                    <ServiceMotif variant="phone" />
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
}
